import React, { useEffect, useState, useRef, useContext } from 'react';
import { Box, ThemeProvider, Typography } from '@mui/material';
import { theme } from '../../../theme/theme';
import SubHeader from './SubHeader';
import { makeStyles } from '@material-ui/core/styles';
import { colors } from '../../../theme/color-palette';
import DVTable from '../../DVTable';
import RootCausePanel from './RootCausePanel/RootCausePanel';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	getRcaBDDMetrics,
	getRcaData,
	resetRcaLoadingState,
	updateInventoryRootCause,
	updateRcaTableHeadersAndRows,
} from '../../../redux/actions/rcaActions';
import ShimmerRCA from '../ShimmerRCA';
import MetricBox from './MetricBox';
import { saveEventTrack } from '../../../redux/actions/eventTrackAction';
import { UserPortalContext } from '../../../context/appcontext';
import { getValueForExcel } from '../../Table/utils';
import ShimmerRepInStock from '../ShimmerRepInStock';
import Tiles from '../../../common/components/tiles';
import {
	performanceSummary,
	GUIDED_INSIGHTS_RCA,
	STORE_DRILL_DOWN_PAGE_LAUNCHED,
	RCA_REPLINSIHMENT_TEXT,
} from '../../../constants/constant';
import MetricHeader from './MetricHeading';
import SomethingWentWrong from '../../../common/components/SomethingWentWrong';
import TextWithDefinition from '../../TextWithDefinition';
import { IDS_TO_COLUMN_MAPPING } from '../../../redux/reducers/constants';
const rootCausesArr = {
	inventoryHealth: {
		root_cause: 'Replenishment Health',
		definitionId: 'inventoryHealth_ih_r',
		impact: -1000000,
		metrics: [
			{
				id: 'replenishment_instock',
				label: 'Replenishable InStock',
				valueRenderer: (value) => value,
				hasEdit: true,
				type: 'item',
				hasDefinition: true,
				definitionId: 'replenishableInstock_ih_r',
			},
			{
				id: 'potential_sales_impact_inventory',
				label: 'Potential Sales Impact',
				type: 'sales',
				hasDefinition: true,
				definitionId: 'potentialSalesImpact_ih_r',
			},
			{
				id: 'avg_replenishable_instock',
				label: 'Avg Replenishable InStock %',
				type: 'percent',
				hasDefinition: true,
				definitionId: 'avgReplenishableInstockPercent_ih_r',
			},
			{
				id: 'avg_nil_pick',
				label: 'Avg NilPick %',
				type: 'percent',
				hasDefinition: true,
				definitionId: 'avgNilPickPercent_ih_r',
			},
		],
	},
	pricing: {
		root_cause: 'Pricing Change',
		definitionId: 'pricingChange_pricing_r',
		impact: -500000,
		metrics: [
			{
				id: 'increased_in_price',
				label: 'Increased in Price',
				type: 'item',
				hasDefinition: true,
				definitionId: 'increasedInPrice_pricing_r',
			},
			{
				id: 'decreased_in_price',
				label: 'Decreased in Price',
				type: 'item',
				hasDefinition: true,
				definitionId: 'descreasedInPrice_pricing_r',
			},
			{
				id: 'potential_sales_impact_price_change',
				label: 'Potential Sales Impact',
				type: 'sales',
				hasDefinition: true,
				definitionId: 'potentialSalesImpact_pricing_r',
			},
		],
	},
	pod: {
		root_cause: 'Points of Distribution',
		definitionId: 'pod_r',
		impact: -300000,
		metrics: [
			{
				id: 'increased_in_traited_stores',
				label: 'Increased in Traited Stores',
				type: 'item',
				hasDefinition: true,
				definitionId: 'increasedInTraitedStore_pod_r',
			},
			{
				id: 'decreased_in_traited_stores',
				label: 'Decreased in Traited Stores',
				type: 'item',
				hasDefinition: true,
				definitionId: 'descreasedInTraitedStore_pod_r',
			},
			{
				id: 'potential_sales_impact_pod',
				label: 'Potential Sales Impact',
				type: 'sales',
				hasDefinition: true,
				definitionId: 'potentialSalesImpact_pod_r',
			},
		],
	},
	assortment: {
		root_cause: 'Assortment Change',
		definitionId: 'assortmentChange_assort_r',
		impact: -80000,
		metrics: [
			{
				id: 'added',
				label: 'Added',
				type: 'item',
				hasDefinition: true,
				definitionId: 'added_assort_r',
			},
			{
				id: 'removed',
				label: 'Removed',
				type: 'item',
				hasDefinition: true,
				definitionId: 'removed_assort_r',
			},
			{
				id: 'potential_sales_impact_assortment_changes',
				label: 'Potential Sales Impact',
				type: 'sales',
				hasDefinition: true,
				definitionId: 'potentialSalesImpact_assort_r',
			},
		],
	},
};

const RootCauseAnalysis = () => {
	const gridRef = useRef();
	const dispatch = useDispatch();
	const portalUserContext = useContext(UserPortalContext);
	const useStyles = makeStyles(() => ({
		subHeader: {
			marginTop: '5px',
		},
		flexibleContainer: {
			border: '1px solid #dadce0',
			borderRadius: '8px',
			margin: '12px',
			marginRight: '30px',
			marginLeft: '30px',
		},
		cards: {
			display: 'flex',
			gap: '16px',
			margin: '12px 30px 32px 30px',
		},
		cardHeaderContainer: {
			display: 'flex',
			margin: '0px 0px 0px 30px',
		},
		cardHeaderIcon: {
			paddingRight: '5px',
			fontSize: '16px',
		},
		cardHeader: {
			fontSize: '18px',
			fontWeight: 700,
			lineHeight: '24px',
		},
		rcaContainer: {
			margin: '8px 0px 0px 0px',
			display: 'flex',
			gap: '24px',
			flexDirection: 'column',
		},
		container: {
			border: `1px solid ${colors.LIGHT_GREY}`,
			borderRadius: '8px',
			height: '120px',
			width: '100%',
		},
		rcTableContainer: {
			border: `1px solid  ${colors.LIGHT_GREY}`,
			// borderRadius: '8px',
			height: '892px',
			display: 'flex',
		},
		rcTableLeftPanel: {
			borderRight: `1px solid  ${colors.LIGHT_GREY}`,
			width: '15%',
			height: '100%',
		},
		rcTable: {
			width: '85%',
			height: '100%',
		},
		errorMsg: {
			marginTop: '10%',
		},
	}));

	const classes = useStyles();
	const navigate = useHistory();

	const {
		tableColumns,
		tableRows,
		rootCauses,
		supplier_share,
		supplier_share_change,
		net_sales,
		net_sales_change,
		net_sales_change_percent,
		walmart_net_sales_change_percent,
		units,
		units_change,
		units_change_percent,
		walmart_units_change_percent,
		avgNilPickPct,
		wmNilPickRate,
		inventory_avg_nilpick_percent,
		inventory_avg_instock_percent,
		inventory_items_oos,
		pricing_increased_in_price,
		pricing_decreased_in_price,
		pricing_potional_sales_impact,
		pod_increased_in_traited_stores,
		pod_decreased_in_traited_stores,
		pod_potential_sales_impact,
		assortment_added,
		assortment_removed,
		assortment_potential_sales_impact,
		inventory_potential_sales_impact,
		loading,
		currentRootCause,
		error,
		repInStockLoading,
		replInstockError,
		threshold,
	} = useSelector((state) => state.rcaReducer);

	const metricTabData = {
		share: supplier_share,
		shareChg: supplier_share_change,
		netSales: net_sales,
		netSalesAbsChg: net_sales_change,
		netSalesPercentChg: net_sales_change_percent,
		units: units,
		units_change: units_change,
		units_change_percent: units_change_percent,
		walmart_units_change_percent: walmart_units_change_percent,
		avgNilPickPct: avgNilPickPct,
		wmNilPickRate: wmNilPickRate,
	};

	let selectedFilters = useSelector(
		(state) => state?.salesReducer?.selectedFilters
	);

	const weekFilters = JSON.parse(sessionStorage.getItem('weekfilters'));

	if (!selectedFilters?.startDate) {
		selectedFilters = weekFilters;
	}

	const handleDownload = () => {
		const downloadTime = new Date().toLocaleString()?.replace(',', ' ');
		gridRef.current?.api?.exportDataAsExcel({
			fileName: `Scintilla-Channel Performance-${downloadTime}`,
			sheetName: 'Data',
			shouldRowBeSkipped(params) {
				return params.node.data?.fullWidth;
			},
			processCellCallback(params) {
				return params.column.colDef.colId === 'ag-Grid-AutoColumn'
					? params?.node?.data?.description
					: getValueForExcel(params);
			},
		});

		const request = {
			event: 'Download',
			event_description: 'Root cause .Xlsx report',
			attributes: GUIDED_INSIGHTS_RCA,
		};
		dispatch(saveEventTrack({ request, portalUserContext }));
	};

	const [
		externalFilters,
		//setExternalFilters
	] = useState({});

	// Holds the state for the root causes tab
	const [tab, setTab] = useState(null);

	// Retrieveing the path parameters from the url
	const { type, id, name, identified, anomalyChange } = useParams();

	const rcaPayload = {
		startDate: selectedFilters?.startDate,
		endDate: selectedFilters?.endDate,
		type: type?.toUpperCase().replace(/ +/g, ''),
		id: [id],
		name: name,
		anomalyChange: anomalyChange,
	};

	// During initial loading
	useEffect(() => {
		if (identified == 'identified') {
			dispatch(getRcaData({ request: rcaPayload }));
		} else {
			dispatch(resetRcaLoadingState());
		}
	}, []);

	useEffect(() => {
		if (rootCauses?.length > 0 && tab !== null) {
			const rootCause = rootCauses[tab]?.domain;
			if (rootCause !== 'assortment') {
				const request = {
					startDate: selectedFilters?.startDate,
					endDate: selectedFilters?.endDate,
					channel: ['STORE'],
				};
				const payload = {
					request: request,
					rootCause: rootCause,
					rootCauses: rootCauses,
				};

				dispatch(getRcaBDDMetrics(payload));
			} else {
				dispatch(
					updateRcaTableHeadersAndRows({
						rootCause: 'assortment',
					})
				);
			}
		}
	}, [tab]);

	// Whenever user clicks on any row in rca page
	const handleTableCellClicked = (data, event) => {
		if (
			event.colDef.id ===
			IDS_TO_COLUMN_MAPPING.IN_STOCK_PERCENTAGE_THIS_YEAR_AVERAGE
		) {
			const request = {
				event: STORE_DRILL_DOWN_PAGE_LAUNCHED,
				event_description: `${RCA_REPLINSIHMENT_TEXT} ${data.node.inStockPctTYAvg}`,
				attributes: GUIDED_INSIGHTS_RCA,
			};
			dispatch(saveEventTrack({ request, portalUserContext }));

			//implement event tracking, check with product on the attributes needed
			navigate.push({
				pathname: `/details/store-drilldown/${type}/${id}/${name}/${identified}/anomalyChange/${anomalyChange}/item-number/${data?.node?.wmItemNbr}/threshold/${threshold}`,
			});
		} else {
			const request = {
				event: 'Item Details Page Launched',
				event_description: 'AI Insights Tab',
				attributes: GUIDED_INSIGHTS_RCA,
			};
			dispatch(saveEventTrack({ request, portalUserContext }));

			const itemName = data?.node?.wmItemNm?.replace('/', '%2F');

			navigate.push({
				pathname: `/details/root-cause-analysis/${type}/${id}/${name}/${identified}/anomalyChange/${anomalyChange}/item-details/${data?.node?.wmItemNbr}/${itemName}`,
			});
		}
	};

	// Generating the payload for rca top cards
	const topCardsData = {
		replenishment_instock: inventory_items_oos,
		potential_sales_impact_inventory: inventory_potential_sales_impact,
		avg_replenishable_instock: inventory_avg_instock_percent,
		avg_nil_pick: inventory_avg_nilpick_percent,
		increased_in_price: pricing_increased_in_price,
		decreased_in_price: pricing_decreased_in_price,
		potential_sales_impact_price_change: pricing_potional_sales_impact,
		increased_in_traited_stores: pod_increased_in_traited_stores,
		decreased_in_traited_stores: pod_decreased_in_traited_stores,
		potential_sales_impact_pod: pod_potential_sales_impact,
		added: assortment_added,
		removed: assortment_removed,
		potential_sales_impact_assortment_changes:
			assortment_potential_sales_impact,
	};

	// updating the ReplInstock percent on the inventory top cards
	const updateReplInstock = (threshold) => {
		const payload = {
			...rcaPayload,
			threshold: threshold,
		};
		dispatch(
			updateInventoryRootCause({
				request: payload,
				rootCauses: rootCauses,
			})
		);
	};

	const parseData = (data = []) => {
		if (data?.edges?.length > 0) {
			return data?.edges?.map((item) => {
				return {
					...item,
					potentialSalesImpact: Number(item.node.potentialSalesImpact)?.toFixed(
						2
					),
					aurTY: Number(item.node.aurTY)?.toFixed(2),
					aurTYChngPctRef: Number(item.node.aurTYChngPctRef)?.toFixed(2),
					unitsTYChngPctRef: Number(item.node.unitsTYChngPctRef)?.toFixed(2),
					inStockPctAvg: Number(item.node.inStockPctAvg)?.toFixed(2),
					nilPikRate: Number(item.node.nilPikRate)?.toFixed(2),
				};
			});
		}
		return data;
	};

	return (
		<ThemeProvider theme={theme}>
			{loading ? (
				<ShimmerRCA />
			) : (
				<Box>
					<Box className={classes.subHeader}>
						<SubHeader />
					</Box>
					{identified === 'notidentified' ? (
						<SomethingWentWrong
							highlightedText={
								'Root causes cannot be identified at this moment'
							}
							text={`We'll bring in additional root causes in the future as the product grows. Stay tuned!`}
						/>
					) : error ? (
						<Box className={classes.errorMsg}>
							<SomethingWentWrong />
						</Box>
					) : (
						<Box>
							<Box className={classes.cardHeaderContainer}>
								<Typography
									variant="bold"
									sx={{
										fontSize: '18px',
										fontWeight: 700,
										lineHeight: '24px',
									}}
									className={classes.cardHeader}>
									Performance Summary
								</Typography>
							</Box>
							<Box className={classes.cards}>
								<Tiles
									title={
										<TextWithDefinition
											text={performanceSummary.SUPPLIER_SHARE}
											hasDefinition={true}
											definitionId={'supplierShare'}
										/>
									}
									share={metricTabData.share}
									shareChg={metricTabData.shareChg}
									comparisonSection={true}
								/>
								<Tiles
									title={
										<TextWithDefinition
											text={performanceSummary.NET_SALES}
											hasDefinition={true}
											definitionId={'netsales'}
										/>
									}
									netSales={`$${Number(
										metricTabData.netSales
									).toLocaleString()}`}
									wmValue={walmart_net_sales_change_percent}
									netSalesAbsChg={metricTabData.netSalesAbsChg}
									netSalesPercentChg={metricTabData.netSalesPercentChg}
									comparisonSection={true}
								/>
								<Tiles
									title={
										<TextWithDefinition
											text={performanceSummary.UNITS}
											hasDefinition={true}
											definitionId={'unitsSold'}
										/>
									}
									netSales={`${Number(metricTabData.units).toLocaleString()}`}
									wmValue={walmart_units_change_percent}
									netSalesAbsChg={metricTabData.units_change}
									netSalesPercentChg={metricTabData.units_change_percent}
									comparisonSection={true}
								/>
								<Tiles
									title={
										<TextWithDefinition
											text={performanceSummary.AVG_NILPICK}
											hasDefinition={true}
											definitionId={'avgNilPickPercent_ih_r'}
										/>
									}
									share={metricTabData.avgNilPickPct}
									wmValue={metricTabData.wmNilPickRate}
									comparisonSection={false}
								/>{' '}
							</Box>
							<Box className={classes.flexibleContainer}>
								<Box className={classes.rcaContainer}>
									<Box className={classes.rcaContainer}>
										<Typography
											sx={{
												fontSize: '18px',
												fontWeight: 700,
												marginLeft: '16px',
											}}>
											Root Causes ({rootCauses?.length})
										</Typography>
									</Box>
									<Box className={classes.rcTableContainer}>
										<Box className={classes.rcTableLeftPanel}>
											<RootCausePanel
												rootCausesArr={rootCauses}
												tab={tab}
												metrices={rootCausesArr[currentRootCause]}
												setTab={setTab}></RootCausePanel>
										</Box>
										<Box className={classes.rcTable}>
											{repInStockLoading ? (
												<ShimmerRepInStock />
											) : (
												<>
													<MetricHeader
														headerName={
															rootCausesArr[currentRootCause]?.root_cause
														}
														definitionId={
															rootCausesArr[currentRootCause]?.definitionId
														}
														handleDownload={handleDownload}
													/>
													<MetricBox
														metrices={rootCausesArr[currentRootCause]}
														data={topCardsData}
														updateReplInstock={updateReplInstock}
													/>
													{replInstockError &&
													currentRootCause === 'inventoryHealth' ? (
														<SomethingWentWrong text={'Try again'} />
													) : (
														<DVTable
															handleCellClicked={handleTableCellClicked}
															columns={tableColumns}
															data={parseData(tableRows)}
															filters={externalFilters}
															tableRef={gridRef}
															initialItemsPerPage={10}
														/>
													)}
												</>
											)}
										</Box>
									</Box>
								</Box>
							</Box>
						</Box>
					)}
				</Box>
			)}
		</ThemeProvider>
	);
};

export default RootCauseAnalysis;
